import { Controller } from '@hotwired/stimulus';
import { DirectUpload } from '@rails/activestorage';
import { track } from '../lib/analytics';

const ACCEPTED_TYPES = ['audio/', 'video/'];

export default class extends Controller {
  static values = {
    genericErrorMessage: String,
    directUploadUrl: String,
    pro: Boolean,
    batchUrl: String,
  };

  static targets = [
    'uploadError',
    'uploadErrorMessage',
    'progressBarWrapper',
    'progressBar',
    'input',
    'uploadFormWrapper',
    'dropOverlay',
    'submitButton',
    'uploadFormContent',
    'configurationStep',
    'progressPercentage',
    'filename',
    'size',
    'fileCard',
    'fileTile',
    'finalStep',
    'compressingWrapper',
    'qualityInput',
    'originalSize',
    'estimatedSize',
    'cardEstimate',
    'invalidFileStep',
    'batchPaywall',
  ];

  static states = {
    INITIAL: 'initial',
    UPLOADING: 'uploading',
    CONFIGURING: 'configuring',
    FINAL: 'final',
  };

  connect() {
    if (this.hasInputTarget) {
      this.inputTarget.value = null;
      this.element.addEventListener('dragenter', this.onDragEnter.bind(this));
      this.element.addEventListener('dragleave', this.onDragExit.bind(this));
      this.element.addEventListener('dragover', this.onDragOver.bind(this));
      this.element.addEventListener('drop', this.onDrop.bind(this));
      this.uploadFormContentTarget.addEventListener('click', this.onContainerClick.bind(this));
    }

    this.currentState = this.constructor.states.INITIAL;
    this.activeXHR = null;
    this.blobXHR = null;
    this.batchMode = false;
    this.createdHiddenInputs = [];
    this.originalFormAction = this.element.querySelector('form')?.action;
    this.fileSizeBytes = 0;
    this.hide('configurationStep');
    this.hide('compressingWrapper');
    this.hide('finalStep');
    this.hide('invalidFileStep');

    // Listen for turbo frame updates to detect compressed file size
    const progressFrame = this.element.querySelector('turbo-frame#progress_button');
    if (progressFrame) {
      progressFrame.addEventListener('turbo:frame-load', () => this.updateOutputFileSize());
    }
  }

  isFileUploadEnabled() {
    return (
      this.currentState === this.constructor.states.INITIAL &&
      !this.uploadFormWrapperTarget.classList.contains('hidden')
    );
  }

  isMediaFile(file) {
    return ACCEPTED_TYPES.some((type) => file.type.startsWith(type));
  }

  onDrop(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isFileUploadEnabled()) {
      return;
    }

    track('Media compressor file dropped');
    const files = Array.from(event.dataTransfer.files);

    if (files.length > 0) {
      this.handleFiles(files);
      this.dropOverlayTarget.classList.remove('opacity-100');
      this.dropOverlayTarget.classList.add('opacity-0', 'pointer-events-none');
    }
  }

  onDragEnter(event) {
    event.preventDefault();

    if (!this.isFileUploadEnabled()) {
      return;
    }

    if (event.dataTransfer.types.includes('Files')) {
      this.dropOverlayTarget.classList.add('opacity-100');
      this.dropOverlayTarget.classList.remove('opacity-0', 'pointer-events-none');
    }
  }

  onDragExit(event) {
    if (!this.isFileUploadEnabled()) {
      return;
    }

    if (event.currentTarget === this.element && !this.element.contains(event.relatedTarget)) {
      this.dropOverlayTarget.classList.remove('opacity-100');
      this.dropOverlayTarget.classList.add('opacity-0', 'pointer-events-none');
    }
  }

  onDragOver(event) {
    if (!this.isFileUploadEnabled()) {
      return;
    }
    event.preventDefault();
  }

  start() {
    this.handleFiles(Array.from(this.inputTarget.files));
  }

  // Single file → classic flow. Multiple files → pro batch, or the batch
  // paywall for everyone else (free users can only get here via drag & drop —
  // their file input has no `multiple`).
  handleFiles(files) {
    if (files.length === 0) return;

    const invalid = files.find((file) => !this.isMediaFile(file));
    if (invalid) {
      this.showInvalidFile(invalid);
      return;
    }

    if (files.length === 1) {
      this.uploadFile(files[0]);
      this.showProgressBar();
    } else if (this.proValue) {
      this.uploadBatch(files);
    } else {
      this.showBatchPaywall();
    }
  }

  showBatchPaywall() {
    track('Media compressor batch paywall shown');
    this.hide('uploadFormContent');
    this.show('batchPaywall');
  }

  uploadBatch(files) {
    track('Media compressor batch upload started', { count: files.length });
    this.batchMode = true;

    const form = this.element.querySelector('form');
    if (form && this.hasBatchUrlValue) {
      form.action = this.batchUrlValue;
    }
    this.createHiddenInput('operation', 'compress');

    this.showProgressBar();
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    this.fileSizeBytes = totalBytes;
    this.filenameTarget.textContent = `${files.length} files`;
    this.sizeTarget.textContent = this.prettyPrintSize(totalBytes);

    let completed = 0;
    files.forEach((file) => {
      const upload = new DirectUpload(file, this.directUploadUrlValue, this);
      upload.create((error, blob) => {
        if (error) {
          this.handleUploadError(error);
          return;
        }
        this.createHiddenInput('files[]', blob.signed_id);
        completed += 1;
        const percentage = Math.floor((completed / files.length) * 100);
        this.progressBarTarget.style.width = `${percentage}%`;
        this.progressPercentageTarget.textContent = `${percentage}%`;

        if (completed === files.length) {
          track('Media compressor batch upload finished', { count: files.length });
          this.hideProgressBar();
          this.showConfigurationStep();
          this.populateSizeEstimates();
    this.markFileTileReady();
        }
      });
    });
  }

  showInvalidFile(file) {
    this.filenameTarget.textContent = file.name;
    this.setFileTile(file.name);
    this.sizeTarget.textContent = this.prettyPrintSize(file.size);
    this.hide('uploadFormContent');
    this.show('fileCard');
    this.show('invalidFileStep');
    this.markFileTileReady();
    track('Media compressor invalid file selected');
  }

  uploadFile(file) {
    const url = this.directUploadUrlValue;
    const upload = new DirectUpload(file, url, this);
    const filename = file.name;

    this.fileSizeBytes = file.size;
    this.filenameTarget.textContent = filename;
    this.sizeTarget.textContent = this.prettyPrintSize(file.size);

    upload.create((error, blob) => {
      if (error) {
        this.handleUploadError(error);
      } else {
        track('Media compressor upload finished');
        this.hideProgressBar();
        this.createHiddenInput(this.inputTarget.name, blob.signed_id);
        this.createHiddenInput('filename', filename);
        this.showConfigurationStep();
        this.populateSizeEstimates();
    this.markFileTileReady();
      }
      this.activeXHR = null;
      this.blobXHR = null;
    });

    track('Media compressor upload started');
  }

  populateSizeEstimates() {
    if (this.hasOriginalSizeTarget) {
      this.originalSizeTarget.textContent = this.prettyPrintSize(this.fileSizeBytes);
    }
    this.updateEstimate();
  }

  updateEstimate() {
    // Update per-card estimates
    if (this.hasCardEstimateTarget) {
      this.cardEstimateTargets.forEach((el) => {
        const reduction = parseInt(el.dataset.reduction, 10) || 50;
        const estimatedBytes = this.fileSizeBytes * ((100 - reduction) / 100);
        el.textContent = `~${this.prettyPrintSize(estimatedBytes)}`;
      });
    }

    // Update legacy single estimate target
    if (this.hasEstimatedSizeTarget) {
      const checked = this.qualityInputTargets.find((input) => input.checked);
      if (!checked) return;
      const reduction = parseInt(checked.dataset.reduction, 10) || 50;
      const estimatedBytes = this.fileSizeBytes * ((100 - reduction) / 100);
      this.estimatedSizeTarget.textContent = `~${this.prettyPrintSize(estimatedBytes)}`;
    }
  }

  selectQuality(event) {
    const label = event.currentTarget;
    const radio = label.querySelector('input[type="radio"]');
    if (radio) {
      radio.checked = true;
      this.updateEstimate();
    }
  }

  createHiddenInput(name, value) {
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('value', value);
    hiddenField.name = name;

    const form = this.element.querySelector('form');
    if (form) {
      form.appendChild(hiddenField);
    } else {
      this.element.appendChild(hiddenField);
    }
    this.createdHiddenInputs.push(hiddenField);
  }

  // Callback method that gets called automatically by the Rails Active Storage DirectUpload
  directUploadWillCreateBlobWithXHR(request) {
    this.blobXHR = request;
  }

  // Callback method that gets called automatically by the Rails Active Storage DirectUpload
  directUploadWillStoreFileWithXHR(request) {
    this.activeXHR = request;
    request.upload.addEventListener('progress', (event) => this.setUploadProgress(event));

    request.addEventListener('error', (event) => {
      this.handleUploadError(event);
    });
  }

  setUploadProgress(event) {
    if (this.batchMode) return; // batch progress counts completed files instead

    const value = Math.min(1, Math.max(0, event.loaded / event.total));
    const percentage = Math.floor(value * 100);
    this.progressBarTarget.style.width = `${percentage}%`;
    this.progressPercentageTarget.textContent = `${percentage}%`;
  }

  showProgressBar() {
    this.currentState = this.constructor.states.UPLOADING;
    this.hide('uploadFormContent');
    this.hide('batchPaywall');
    this.show('progressBarWrapper');
    this.show('fileCard');
    this.hide('configurationStep');
    this.hide('compressingWrapper');
    this.hide('invalidFileStep');
    this.disableSubmitButton();
    this.hide('finalStep');
    this.markFileTilePending();
    this.scrollToTop();
  }

  hideProgressBar() {
    this.progressBarTarget.style.width = '0%';
    this.progressPercentageTarget.textContent = '0%';
    this.hide('progressBarWrapper');
    this.show('fileCard');
    this.enableSubmitButton();
  }

  showConfigurationStep() {
    this.currentState = this.constructor.states.CONFIGURING;
    this.hide('progressBarWrapper');
    this.hide('uploadFormContent');
    this.hide('finalStep');
    this.hide('invalidFileStep');
    this.show('configurationStep');
    this.enableSubmitButton();
    this.scrollToTop();
  }

  hideConfigurationStep() {
    this.hide('configurationStep');
    this.show('compressingWrapper');
    this.disableSubmitButton();
  }

  showFinalStep() {
    this.currentState = this.constructor.states.FINAL;
    this.hide('configurationStep');
    this.hide('compressingWrapper');
    this.show('finalStep');
    this.enableSubmitButton();
  }

  updateOutputFileSize() {
    const el = this.element.querySelector('[data-output-byte-size]');
    if (!el) return;

    if (this.hasSizeTarget) {
      const bytes = parseInt(el.dataset.outputByteSize, 10);
      if (bytes > 0) {
        this.sizeTarget.textContent = this.prettyPrintSize(bytes);
      }
    }
  }

  disableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
    }
  }

  enableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
    }
  }

  prettyPrintSize(size) {
    const mb = size / 1000000.0;

    if (mb < 1) {
      return `${parseFloat(mb.toFixed(2))} MB`;
    } else {
      return `${parseFloat(mb.toFixed(1))} MB`;
    }
  }

  resetFileInput() {
    if (this.hasInputTarget) {
      this.inputTarget.value = null;
      this.filenameTarget.textContent = '';
      this.sizeTarget.textContent = '';
      this.hideProgressBar();
    }

    this.createdHiddenInputs.forEach((input) => input.remove());
    this.createdHiddenInputs = [];
    this.batchMode = false;

    const form = this.element.querySelector('form');
    if (form && this.originalFormAction) {
      form.action = this.originalFormAction;
    }
  }

  deleteFile(event) {
    this.hide('uploadError');
    const stage = document.getElementById('progress_button');
    if (stage) stage.innerHTML = '';
    this.currentState = this.constructor.states.INITIAL;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.blobXHR) {
      this.blobXHR.abort();
      this.blobXHR = null;
    }

    if (this.activeXHR) {
      this.activeXHR.abort();
      this.activeXHR = null;
    }

    this.fileSizeBytes = 0;
    this.resetFileInput();
    this.hide('configurationStep');
    this.hide('compressingWrapper');
    this.hide('finalStep');
    this.hide('invalidFileStep');
    this.hide('fileCard');
    this.hide('batchPaywall');
    this.show('uploadFormContent');

    track('Media compressor upload cancelled');
  }

  onContainerClick(event) {
    if (!this.isFileUploadEnabled()) {
      return;
    }

    // Don't trigger if clicking on the file input or its label.
    // Otherwise, the open file dialog will be triggered twice.
    if (event.target === this.inputTarget || event.target.id === 'label-file') {
      return;
    }

    this.inputTarget.click();
  }

  handleUploadError(error) {
    console.error('Upload error:', error);
    this.showInlineError(this.genericErrorMessageValue);
  }

  showInlineError(message) {
    this.resetToStart();
    if (this.hasUploadErrorMessageTarget) this.uploadErrorMessageTarget.textContent = message;
    this.show('uploadError');
  }

  // Reset the flow without hiding the error banner (deleteFile clears it).
  resetToStart() {
    const saved = this.hasUploadErrorTarget && !this.uploadErrorTarget.classList.contains('hidden');
    this.deleteFile();
    if (saved) this.show('uploadError');
  }

  show(targetName) {
    const hasTarget = this[`has${this.capitalizeFirst(targetName)}Target`];

    if (!hasTarget) return;

    const target = this[`${targetName}Target`];
    const targets = this[`${targetName}Targets`];
    if (targets) {
      targets.forEach((target) => {
        target.classList.remove('hidden');
      });
    } else if (target) {
      target.classList.remove('hidden');
    }
  }

  hide(targetName) {
    const hasTarget = this[`has${this.capitalizeFirst(targetName)}Target`];

    if (!hasTarget) return;
    const target = this[`${targetName}Target`];
    const targets = this[`${targetName}Targets`];
    if (targets) {
      targets.forEach((target) => {
        target.classList.add('hidden');
      });
    } else if (target) {
      target.classList.add('hidden');
    }
  }

  scrollToTop() {
    const nav = document.querySelector('nav, header');
    const offset = nav ? nav.offsetHeight + 16 : 16;
    const top = this.element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  capitalizeFirst(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  setFileTile(filename) {
    const ext = (filename.includes('.') ? filename.split('.').pop() : 'FILE').slice(0, 5).toUpperCase();
    this.fileTileTarget.textContent = ext || 'FILE';
  }

  markFileTileReady() {
    this.fileTileTarget.classList.remove('animate-pulse');
  }

  markFileTilePending() {
    this.fileTileTarget.classList.add('animate-pulse');
  }
}
